import { SUPPORT_PAGE_PATH } from '@croissants/contracts/qr';
import { type JSX, Suspense } from 'react';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { Loading } from './components/feedback';
import { Shell } from './components/Shell';
import { useImmediateWindow } from './hooks/useImmediateWindow';
import { GUEST_LEGAL_ROUTES } from './lib/config';
import { entryStep } from './lib/journey';
import {
  InvalidCode,
  OrderingClosed,
  ResolvingSession,
  ScanPrompt,
  Unavailable,
  Unreachable,
} from './screens/Landing';
import { Basket, Checkout, LegalPage, OrderDetail, Orders, Support, Windows } from './screens/lazy';
import { Menu } from './screens/Menu';
import { Shops } from './screens/Shops';
import { useBasket } from './state/basket-provider';
import { useCheckoutDraft } from './state/checkout';
import { useSession } from './state/session';

/**
 * `order.` - the QR storefront (PRD section 4.1).
 *
 * The whole journey is scan, choose, schedule, pay - under three minutes, on a
 * phone, with no account. The router is deliberately flat for that reason: every
 * screen is one segment from the entry point, and the back button always means
 * what it looks like it means.
 *
 * `/windows` is the day-and-time picker. In every mode the shelf comes first
 * (`entryStep` in `lib/journey.ts`) and the picker is asked on the way out of
 * the basket (`checkoutStep`) - in mode B2 from the delivery popup's "Schedule
 * for later" - scoped in mode B to the brand the guest picked. One screen,
 * never two implementations. The same route is the edit
 * step later, reached from the basket and the review with `?next=` naming the
 * screen to come back to, which is what lets a guest change their morning
 * without losing a basket.
 *
 * Nothing renders until the QR session has resolved, because the session decides
 * which journey exists at all: mode A and mode B2 open on the shelf, mode B on
 * a list of brands, and an address that cannot yet be served opens on the defined not-yet-available
 * screen rather than on a broken storefront (GST-028, GST-040).
 */
export function App(): JSX.Element {
  return (
    <Shell>
      {/* Inside the shell, for the screens past the shelf (`screens/lazy.ts`):
          a cold link to one of them shows the app's own loading line while its
          chunk arrives. */}
      <Suspense fallback={<Loading />}>
        <Switch>
          {/*
          ABOVE the session gate, and the only route that is (GST-043): the small
          code on the back of the printed card opens this page in a browser that
          may never have scanned an apartment, and a guest the gate has turned
          away is exactly the guest who needs to reach somebody.
        */}
          <Route path={SUPPORT_PAGE_PATH} component={Support} />
          {/* The guest's legal documents (WEB-007), one route per document and
              language from the legal register: readable before any scan too. */}
          {GUEST_LEGAL_ROUTES.map(({ id, locale, path }) => (
            <Route key={path} path={path}>
              {() => <LegalPage id={id} locale={locale} />}
            </Route>
          ))}
          <Route>
            <Gated />
          </Route>
        </Switch>
      </Suspense>
    </Shell>
  );
}

function Gated(): JSX.Element {
  return (
    <SessionGate>
      <Switch>
        <Route path="/" component={Home} />
        {/*
          Mode B2 had a first screen here for one day (2026-09-20). A scan opens
          the shelf now and the delivery time is asked by a popup over it
          (GST-045), so a phone still holding the old address lands on the
          journey's own first step.
        */}
        <Route path="/start">
          <Redirect to="/" replace />
        </Route>
        <Route path="/shops" component={Shops} />
        <Route path="/menu" component={Menu} />
        <Route path="/basket" component={Basket} />
        <Route path="/windows" component={Windows} />
        <Route path="/checkout" component={Checkout} />
        {/*
          There is no `/pay` route any more. Payment happens in a sheet on
          the review screen itself (GST-005), so the separate screen that
          used to take a handoff object across a navigation had nothing left
          to hand off - it could only ever have redirected. A card that
          insists on a 3-D Secure page of its own returns to the guest's
          order, which by then knows whether the payment succeeded.
        */}
        <Route path="/orders" component={Orders} />
        {/*
          There is no in-app receipt SCREEN any more. The receipt is a PDF the
          worker renders and the order page opens directly (PAY-022); a screen
          that restated the same breakdown and offered to print it was the
          intermediate step "Download receipt" existed to remove. A stale link
          to this path falls through to the catch-all below.
        */}
        <Route path="/orders/:orderPublicId">
          {(params) => <OrderDetail orderPublicId={params.orderPublicId ?? ''} />}
        </Route>
        <Route>
          <Redirect to="/" replace />
        </Route>
      </Switch>
    </SessionGate>
  );
}

/**
 * The QR session is the only credential (PRD GST-001). Every state it can be in
 * has its own screen, none of them is a stack trace, and none of them is a
 * spinner with nothing behind it: the state-to-screen mapping is a pure function
 * in `lib/session-screen` with its own tests, so a resolve that is unsettled but
 * not running lands on a screen the guest can act on rather than on the
 * resolving spinner.
 */
function SessionGate({ children }: { children: JSX.Element }): JSX.Element {
  const { screen } = useSession();

  switch (screen) {
    case 'SCAN':
      return <ScanPrompt />;
    case 'RESOLVING':
      return <ResolvingSession />;
    case 'UNREACHABLE':
      return <Unreachable />;
    case 'INVALID':
      return <InvalidCode />;
    case 'NOT_SERVABLE':
      return <Unavailable reason="NOT_SERVABLE" />;
    case 'SERVICE_PAUSED':
      return <Unavailable reason="SERVICE_PAUSED" />;
    /*
     * Nothing can be ordered anywhere (OPS-025, GST-042) - but an order already
     * placed is still being made, still moving and still cancellable, so "your
     * orders" is the one part of the app this screen does not stand in front
     * of. A guest whose croissants are ten minutes away must not meet a closed
     * sign where their tracking page was.
     */
    case 'ORDERING_CLOSED':
      return <OrderingClosedGate>{children}</OrderingClosedGate>;
    default:
      return (
        <>
          {/*
            The picker step of a journey that has no picker (GST-042): with
            immediate delivery alone the draft's morning is today and its window
            is the soonest one, settled from the picker's own answer rather than
            asked. Mounted behind the gate, so a session that opens no
            storefront never asks for a window list it could not be sold.
          */}
          <ImmediateWindow />
          {children}
        </>
      );
  }
}

/** Everything is closed except the orders a guest already has. */
function OrderingClosedGate({ children }: { children: JSX.Element }): JSX.Element {
  const [location] = useLocation();
  return location.startsWith('/orders') ? children : <OrderingClosed />;
}

/** Runs `useImmediateWindow` without giving any screen a reason to know about it. */
function ImmediateWindow(): null {
  useImmediateWindow();
  return null;
}

/**
 * Where a scan opens. The step order itself is `entryStep` (lib/journey.ts),
 * which the vitrine's own guard also asks - stating it once is what stops the
 * two disagreeing and bouncing a guest between them.
 */
function Home(): JSX.Element {
  const { session } = useSession();
  const { basket } = useBasket();
  const { draft } = useCheckoutDraft();
  return (
    <Redirect
      to={entryStep({
        storefrontMode: session?.storefrontMode ?? null,
        shopId: basket.shopId,
        serviceDay: draft.serviceDay,
        windowStart: draft.windowStart,
        orderingModes: session?.orderingModes ?? null,
      })}
      replace
    />
  );
}
